import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { lastEffective, mergeExternal, mergeLogs, reduce, type Action, type ActionBody, type ExternalMergeResult, type Tournament } from '@airmail/engine';
import { istAktuell, istOhneCloud, loadTournament, saveTournament } from './db';
import {
  DEFAULT_API,
  canUndoSynced,
  enableSync as enableSyncRemote,
  loadSync,
  nachziehen,
  nextActionId,
  outboxAusLog,
  removeSync,
  saveSync,
  syncOnce,
  wesentlichAnders,
  type SyncConfig,
} from './sync';
import { currentUser } from './auth';
import { t as tr, useT } from '@/i18n';
import { tm } from '@/i18n/engine';
import { Button } from '@/ui';

/**
 * Fenster desselben Browsers halten sich gegenseitig auf dem Laufenden
 * (docs/notes/beamer.md, „Zweiter Bildschirm"): Laptop mit der Leitung,
 * daneben ein zweites Fenster im Vollbild auf dem Fernseher. Beide lesen
 * dieselbe IndexedDB — nur wusste das zweite Fenster bisher nicht, **wann**
 * es neu lesen soll, und zeigte den Stand vom Öffnen, bis jemand F5 drückte.
 *
 * Jetzt meldet jedes Fenster nach dem Speichern die Turnier-Kennung über einen
 * `BroadcastChannel`; die anderen laden den Stand aus der Datenbank nach. Ohne
 * Server, ohne Umweg über die Cloud, auch offline in der Halle.
 *
 * Nachrichten: `{ id }` Stand gespeichert · `{ id, sync: true }` Sync-Profil
 * gespeichert · `{ id, patch }` Profil-Änderung aus einem Fenster, das nicht
 * selbst abgleicht · `{ id, aus: true }` Sync auf diesem Gerät beendet.
 */
const KANAL = 'airmail.turnier';
type Meldung = { id?: string; sync?: true; aus?: true; patch?: Partial<SyncConfig> };
let kanal: BroadcastChannel | null | undefined;
function kanalHolen(): BroadcastChannel | null {
  if (kanal !== undefined) return kanal;
  try {
    kanal = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(KANAL);
  } catch {
    kanal = null;
  }
  return kanal;
}
function senden(m: Meldung): void {
  try {
    kanalHolen()?.postMessage(m);
  } catch {
    /* dann eben nicht — das andere Fenster holt es beim nächsten Abgleich */
  }
}
function melden(id: string): void {
  senden({ id });
}
/** Derselbe Stand? Länge und letzte Kennung des Protokolls genügen als Vergleich */
function gleicherStand(a: Tournament, b: Tournament): boolean {
  const la = a.log[a.log.length - 1];
  const lb = b.log[b.log.length - 1];
  return a.log.length === b.log.length && la?.id === lb?.id && la?.at === lb?.at;
}

/**
 * Nur **ein** Fenster je Turnier gleicht ab (Web Locks). Vorher lief in jedem
 * Fenster desselben Browsers — Leitung und Beamer — eine eigene Sync-Schleife:
 * doppelte Anfragen, und beide schrieben dasselbe Sync-Profil über. Die
 * anderen Fenster bekommen den Stand über den Kanal; was sie selbst eintragen,
 * nimmt das abgleichende Fenster in seine Outbox (`outboxAusLog`). Ohne Web
 * Locks (ältere Browser) gleicht wie bisher jedes Fenster selbst ab.
 */
const locksDa = (): boolean => typeof navigator !== 'undefined' && !!navigator.locks && typeof navigator.locks.request === 'function';

export interface DispatchResult {
  ok: boolean;
  error?: string;
  warnings: string[];
}

export interface Toast {
  id: number;
  kind: 'ok' | 'warn' | 'error';
  text: string;
  /** Ziel beim Anklicken — ein Hinweis, der auf etwas zeigt, soll auch hinführen */
  to?: string;
}

export type SyncStatus = 'off' | 'idle' | 'syncing' | 'pending' | 'error' | 'offline';

export interface SyncState {
  cfg: SyncConfig | null;
  status: SyncStatus;
  pending: number;
  lastSyncAt?: number;
  lastError?: string;
  syncNow: () => Promise<void>;
  enable: (apiUrl: string) => Promise<void>;
  disable: () => Promise<void>;
}

/** Stand und Aktionen — ändert sich mit jeder Eingabe, nicht mit jedem Abgleich */
export interface TurnierCtx {
  tournament: Tournament;
  /** Aktion ausführen, persistieren; Fehler werden als Toast gezeigt und zurückgegeben */
  dispatch: (body: ActionBody, opts?: { silent?: boolean }) => DispatchResult;
  undo: () => void;
  canUndo: boolean;
  /** Zurückgenommenes wieder anwenden — wie „Wiederholen" in einem Textprogramm */
  redo: () => void;
  canRedo: boolean;
  /** Kurzer Hinweis unten rechts — statt eines blockierenden `alert` */
  notify: (kind: Toast['kind'], text: string, to?: string) => void;
  /** Turnier komplett ersetzen (Backup-Import) */
  replace: (t: Tournament) => void;
  lastSavedAt: number | undefined;
  /**
   * Felder der Sync-Konfiguration ändern (z. B. übernommene Anmeldungen merken).
   * Arbeitet immer auf dem aktuellen Stand — nie auf einer Kopie aus dem Render,
   * sonst gehen gerade eingereihte Aktionen aus der Outbox verloren.
   */
  patchSyncCfg: (patch: Partial<Omit<SyncConfig, 'tournamentId' | 'outbox' | 'remote' | 'seq'>>) => void;
  /** Aktionen eines anderen Geräts einspielen (Datei-Übertragung, docs/17) — landen auch in der Sync-Outbox */
  importExternal: (actions: Action[]) => ExternalMergeResult | null;
}

export interface ToastCtx {
  toasts: Toast[];
  dismissToast: (id: number) => void;
}

/** Alles zusammen — so, wie `useTournament()` es seit jeher liefert */
interface Ctx extends TurnierCtx, ToastCtx {
  sync: SyncState;
}

/*
 * Drei Kontexte statt einem: Stand/Aktionen, Sync-Zustand, Hinweise. Vorher
 * zog jeder Abgleich (alle 8 s: „syncing" → neues Profil → „idle") ein neues
 * Kontext-Objekt nach sich und rendert alle ~22 Verbraucher zwei-, dreimal neu,
 * auch ohne jede Änderung am Turnier. `useTournament()` liefert weiterhin
 * alles; wer nur Stand und Aktionen braucht, nimmt `useTurnier()`.
 */
const TurnierContext = createContext<TurnierCtx | null>(null);
const SyncContext = createContext<SyncState | null>(null);
const ToastContext = createContext<ToastCtx | null>(null);

let toastSeq = 0;
const SYNC_INTERVAL_MS = 8_000;
/** Erst nach dieser Zeit zeigt die Oberfläche „Abgleich läuft" — kurze Abgleiche flackern sonst nur */
const SYNCING_ANZEIGE_MS = 400;

export function TournamentProvider({ id, children, fallback }: { id: string; children: ReactNode; fallback: ReactNode }) {
  const t = useT();
  const [tournament, setTournament] = useState<Tournament | null | undefined>(undefined);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [lastSavedAt, setLastSavedAt] = useState<number | undefined>(undefined);
  const [syncCfg, setSyncCfg] = useState<SyncConfig | null>(null);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('off');
  const [ladeVersuch, setLadeVersuch] = useState(0);
  const stateRef = useRef<Tournament | null>(null);
  const cfgRef = useRef<SyncConfig | null>(null);
  const syncing = useRef(false);
  /** der laufende Abgleich — ein Rückgängig wartet darauf, wenn die Aktion unterwegs sein könnte */
  const syncLauf = useRef<Promise<void> | null>(null);
  /** Eigene Aktionen, die während eines laufenden Abgleichs eingetragen wurden (siehe `syncNow`) */
  const waehrendSync = useRef<Action[]>([]);
  /** Profil-Änderungen während eines Abgleichs — sonst überschriebe sie dessen Ergebnis */
  const patchWaehrend = useRef<Partial<SyncConfig>>({});
  /** Gleicht dieses Fenster ab (hält die Sperre)? Ohne Web Locks immer. */
  const halterRef = useRef(!locksDa());
  const [halter, setHalter] = useState(!locksDa());
  const alive = useRef(true);

  const pushToast = useCallback((kind: Toast['kind'], text: string, to?: string) => {
    const toast: Toast = { id: ++toastSeq, kind, text, ...(to ? { to } : {}) };
    /*
     * Fehler bleiben stehen, bis man sie wegtippt — „Speichern fehlgeschlagen"
     * war sonst weg, bevor man es gelesen hatte. Damit sie sich nicht stapeln,
     * bleiben höchstens die letzten drei.
     */
    if (kind === 'error') {
      setToasts((ts) => {
        const fehler = ts.filter((x) => x.kind === 'error');
        const weg = new Set(fehler.slice(0, Math.max(0, fehler.length - 2)).map((x) => x.id));
        return [...ts.filter((x) => !weg.has(x.id)), toast];
      });
      return;
    }
    setToasts((ts) => [...ts, toast]);
    // Hinweise mit Ziel bleiben länger stehen — sie wollen angeklickt werden. Warnungen
    // je nach Länge 8–20 s statt 4,5 s (Harald: „verschwinden nach wenigen Sekunden“)
    const dauer = to ? 12000 : kind === 'warn' ? Math.min(20000, Math.max(8000, 4000 + text.length * 60)) : 4500;
    window.setTimeout(() => setToasts((ts) => ts.filter((x) => x.id !== toast.id)), dauer);
  }, []);

  /*
   * Speichern gebündelt: Mehrere Eingaben im selben Takt (die Feldzuweisung
   * startet gern drei Spiele auf einmal) schrieben vorher dreimal den ganzen
   * Stand. Jetzt wird am Ende des Takts einmal geschrieben — noch in derselben
   * Aufgabe des Browsers, also ohne Zeitfenster, in dem etwas verloren ginge.
   * `saveTournament` schreibt dabei den Stand ohne Protokoll und hängt nur die
   * neuen Protokoll-Einträge an (store/db.ts, Schema-Version 4).
   */
  const speichernGeplant = useRef(false);
  const persist = useCallback(
    (next: Tournament) => {
      stateRef.current = next;
      setTournament(next);
      if (speichernGeplant.current) return;
      speichernGeplant.current = true;
      queueMicrotask(() => {
        speichernGeplant.current = false;
        const s = stateRef.current;
        if (!s) return;
        saveTournament(s)
          .then(() => {
            if (alive.current) setLastSavedAt(Date.now());
            // erst melden, wenn der Stand in der Datenbank liegt — sonst läse das andere Fenster den alten
            melden(s.id);
          })
          .catch((e: unknown) => pushToast('error', `Speichern fehlgeschlagen: ${(e as Error).message}`));
      });
    },
    [pushToast],
  );

  /**
   * Profil setzen. Hat sich außer der Uhrzeit des letzten Abgleichs nichts
   * geändert, bekommt die Oberfläche nur einen neuen Stand, wenn sich die
   * angezeigte Minute ändert — sonst rendert jeder Abgleich alles neu.
   */
  const setCfg = useCallback((cfg: SyncConfig | null) => {
    const vorher = cfgRef.current;
    cfgRef.current = cfg;
    if (vorher && cfg && !wesentlichAnders(vorher, cfg) && Math.floor((vorher.lastSyncAt ?? 0) / 60_000) === Math.floor((cfg.lastSyncAt ?? 0) / 60_000)) return;
    setSyncCfg(cfg);
  }, []);

  /** Profil speichern, gebündelt wie der Stand — nur im abgleichenden Fenster (sonst überschriebe es dessen Outbox) */
  const cfgSpeichernGeplant = useRef(false);
  const cfgSpeichern = useCallback(() => {
    if (!halterRef.current || cfgSpeichernGeplant.current) return;
    cfgSpeichernGeplant.current = true;
    queueMicrotask(() => {
      cfgSpeichernGeplant.current = false;
      const c = cfgRef.current;
      if (c && halterRef.current) void saveSync(c).then(() => senden({ id: c.tournamentId, sync: true }));
    });
  }, []);

  // --- Laden -----------------------------------------------------------------
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    let lebt = true;
    setTournament(undefined);
    setLadeFehler(null);
    (async () => {
      const [t0, cfg0] = await Promise.all([loadTournament(id), loadSync(id)]);
      if (!lebt) return;
      // Was im Protokoll steht, aber weder beim Server noch in der Outbox, kommt in die Outbox (siehe `outboxAusLog`)
      const cfg = cfg0 && t0 ? outboxAusLog(cfg0, t0.log) : (cfg0 ?? null);
      stateRef.current = t0 ?? null;
      cfgRef.current = cfg;
      setSyncCfg(cfg);
      setSyncStatus(cfg ? (cfg.outbox.length ? 'pending' : 'idle') : 'off');
      setTournament(t0 ?? null);
      // Angemeldet und online, aber noch nicht in der Cloud → im Hintergrund ins Konto sichern (docs/16)
      if (t0 && !cfg0 && currentUser() && DEFAULT_API && navigator.onLine) void autoSichern();
    })().catch((e: unknown) => {
      // IndexedDB kaputt, voll oder gesperrt: nicht ewig „Lade …" zeigen
      if (lebt) setLadeFehler((e as Error)?.message ?? String(e));
    });
    /**
     * Automatische Cloud-Sicherung. Vorher wartete das Öffnen bis zu 6 s darauf
     * und gab dann auf — die Sicherung lief aber weiter und legte später ein
     * Profil an, dem alle Eingaben der Zwischenzeit fehlten (und die Kennungen
     * im Stand passten nicht mehr zu denen in der Outbox). Jetzt: Stand sofort
     * zeigen, Kennungen sofort vergeben, und wenn die Sicherung fertig ist,
     * alles nachtragen, was inzwischen eingegeben wurde.
     */
    async function autoSichern(): Promise<void> {
      try {
        const cur = stateRef.current;
        if (!cur || cfgRef.current || (await loadSync(id))) return;
        // Aus der Cloud gelöscht, lokal behalten („nicht in der Cloud", store/db.ts): nicht wieder hochladen
        if (await istOhneCloud(id)) return;
        const withIds = { ...cur, log: cur.log.map((a) => ({ ...a, id: nextActionId() })) };
        persist(withIds);
        const cfg = await enableSyncRemote(withIds, DEFAULT_API);
        // Seite schon verlassen: das gespeicherte Profil gleicht das nächste Laden an (`outboxAusLog`)
        if (!lebt || cfgRef.current || !stateRef.current) return;
        const angeglichen = outboxAusLog(cfg, stateRef.current.log);
        setCfg(angeglichen);
        if (angeglichen !== cfg) cfgSpeichern();
        setSyncStatus(angeglichen.outbox.length ? 'pending' : 'idle');
      } catch (e) {
        console.warn('Automatische Cloud-Sicherung später', e);
      }
    }
    return () => {
      lebt = false;
    };
    // persist/setCfg/cfgSpeichern sind stabil; neu laden nur bei anderem Turnier oder „Neu laden"
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, ladeVersuch]);

  /*
   * Ein anderes Fenster hat gespeichert: Stand aus der Datenbank übernehmen —
   * ohne selbst zu speichern und zu melden, sonst schaukelten sich die Fenster
   * gegenseitig auf. Mehrere Meldungen kurz nacheinander (die Feldzuweisung
   * schiebt gern drei Spiele auf einmal nach) werden zu einem Nachladen.
   */
  useEffect(() => {
    const k = kanalHolen();
    if (!k) return;
    let timer = 0;
    let cfgTimer = 0;
    const nachladen = async () => {
      // Nur die Zusammenfassung lesen: steht noch der eigene Stand drin, gibt es nichts zu laden
      if (await istAktuell(id)) return;
      const geladen = await loadTournament(id);
      const cur = stateRef.current;
      if (!geladen || !cur || gleicherStand(cur, geladen)) return;
      stateRef.current = geladen;
      setTournament(geladen);
      /*
       * Outbox an den übernommenen Stand angleichen: Was das andere Fenster
       * zurückgenommen hat, darf von hier aus nicht wieder zum Server; was es
       * eingetragen hat (es gleicht ja nicht selbst ab), geht von hier aus mit.
       */
      const cfg = cfgRef.current;
      if (cfg) {
        const next = outboxAusLog(cfg, geladen.log);
        if (next !== cfg) {
          // Läuft gerade ein Abgleich, zählen die übernommenen Eingaben wie hier getippte (siehe `syncNow`)
          if (syncing.current) {
            const alt = new Set(cfg.outbox.map((a) => a.id));
            waehrendSync.current.push(...next.outbox.filter((a) => !alt.has(a.id)));
          }
          setCfg(next);
          if (halterRef.current) {
            cfgSpeichern();
            if (next.outbox.length) window.setTimeout(() => void syncNowRef.current(), 300);
          }
        }
      }
    };
    // Fenster, das nicht abgleicht: Profil (Outbox, Stand der Anzeige) aus der Datenbank
    const profilNachladen = async () => {
      if (halterRef.current) return;
      const c = await loadSync(id);
      if (!c || halterRef.current) return;
      const next = stateRef.current ? outboxAusLog(c, stateRef.current.log) : c;
      setCfg(next);
      setSyncStatus(next.outbox.length ? 'pending' : 'idle');
    };
    const onMessage = (e: MessageEvent<Meldung>) => {
      const m = e.data;
      if (m?.id !== id) return;
      if (m.aus) {
        cfgRef.current = null;
        setSyncCfg(null);
        setSyncStatus('off');
        return;
      }
      if (m.patch) {
        const cur = cfgRef.current;
        if (!cur) return;
        setCfg({ ...cur, ...m.patch });
        if (syncing.current) patchWaehrend.current = { ...patchWaehrend.current, ...m.patch };
        cfgSpeichern();
        return;
      }
      if (m.sync) {
        window.clearTimeout(cfgTimer);
        cfgTimer = window.setTimeout(() => void profilNachladen(), 80);
        return;
      }
      window.clearTimeout(timer);
      timer = window.setTimeout(() => void nachladen().catch((e: unknown) => console.warn('Nachladen aus der Datenbank fehlgeschlagen', e)), 80);
    };
    k.addEventListener('message', onMessage);
    return () => {
      k.removeEventListener('message', onMessage);
      window.clearTimeout(timer);
      window.clearTimeout(cfgTimer);
    };
  }, [id, setCfg, cfgSpeichern]);

  // --- Sync-Durchlauf ------------------------------------------------------
  const syncNow = useCallback((): Promise<void> => {
    const cfg = cfgRef.current;
    const cur = stateRef.current;
    if (!cfg || !cur) return Promise.resolve();
    if (syncing.current) return syncLauf.current ?? Promise.resolve();
    // Ein Fenster, das die Sperre nicht hält, gleicht nicht selbst ab (siehe `locksDa`)
    if (!halterRef.current) return Promise.resolve();
    if (!navigator.onLine) {
      setSyncStatus('offline');
      return Promise.resolve();
    }
    syncing.current = true;
    const anzeige = window.setTimeout(() => setSyncStatus('syncing'), SYNCING_ANZEIGE_MS);
    waehrendSync.current = [];
    patchWaehrend.current = {};
    const lauf = (async () => {
      try {
        const out = await syncOnce(cfg, cur);
        /*
         * Der Abgleich lief auf einem Schnappschuss. Was in der Zwischenzeit hier
         * eingetragen wurde, steht schon im Stand und in `cfgRef`, aber nicht in
         * `out` — es käme sonst weder in die Outbox noch, falls der Abgleich den
         * Stand neu baut, in den Stand: Die Eingabe wäre lokal da und für den
         * Server nie geschehen.
         */
        const dazwischen = waehrendSync.current;
        waehrendSync.current = [];
        let cfgNeu = nachziehen(out.cfg, dazwischen);
        let stateNeu = out.state;
        if (stateNeu && dazwischen.length) {
          /*
           * Den neu gebauten Stand samt Zwischen-Eingaben aufsetzen — mit dem
           * strengen `reduce` obendrauf statt eines zweiten vollen Replays.
           * Passt eine nicht mehr, wie bisher: tolerant zusammenführen; sie
           * bleibt in der Outbox und der nächste Abgleich meldet sie als überholt.
           */
          const inOutbox = new Set(cfgNeu.outbox.map((a) => a.id));
          let s = stateNeu;
          let alle = true;
          for (const a of dazwischen) {
            if (!inOutbox.has(a.id)) continue;
            const r = reduce(s, a);
            if (!r.ok) {
              alle = false;
              break;
            }
            s = r.state;
          }
          stateNeu = alle ? s : mergeLogs({ init: cur.init, remote: cfgNeu.remote, outbox: cfgNeu.outbox }).state;
        }
        // Outbox am Ende an den Stand angleichen (Rückgängig während des Abgleichs, andere Fenster)
        cfgNeu = outboxAusLog(cfgNeu, (stateNeu ?? stateRef.current ?? cur).log);
        const patch = patchWaehrend.current;
        patchWaehrend.current = {};
        if (Object.keys(patch).length) cfgNeu = { ...cfgNeu, ...patch };
        if (!alive.current || cfgRef.current === null) return;
        if (wesentlichAnders(out.cfg, cfgNeu)) void saveSync(cfgNeu);
        const vorher = cfgRef.current;
        setCfg(cfgNeu);
        if (wesentlichAnders(vorher, cfgNeu)) senden({ id, sync: true });
        if (stateNeu) persist(stateNeu);
        // Der Hinweis führt direkt zur Liste, in der beide Stände nebeneinander stehen
        if (out.rejected > 0) {
          const text =
            out.rejected === 1
              ? tr('Eine eigene Eingabe wurde von einem anderen Gerät überholt — hier tippen und entscheiden, welcher Stand gilt')
              : tr('{n} eigene Eingaben wurden von einem anderen Gerät überholt — hier tippen und entscheiden, welcher Stand gilt', { n: out.rejected });
          pushToast('warn', text, `/t/${id}/einstellungen?tab=cloud&konflikt=1`);
        }
        setSyncStatus(cfgNeu.outbox.length ? 'pending' : 'idle');
      } catch (e) {
        // Auf den **aktuellen** Stand des Profils — nicht auf den Schnappschuss vom Start,
        // sonst fielen die Eingaben der Zwischenzeit aus der Outbox
        const basis = cfgRef.current;
        waehrendSync.current = [];
        patchWaehrend.current = {};
        if (basis && alive.current) {
          setCfg({ ...basis, lastError: (e as Error).message });
          setSyncStatus('error');
        }
      } finally {
        window.clearTimeout(anzeige);
        syncing.current = false;
        syncLauf.current = null;
      }
    })();
    syncLauf.current = lauf;
    return lauf;
  }, [id, persist, pushToast, setCfg]);
  const syncNowRef = useRef(syncNow);
  syncNowRef.current = syncNow;

  // Zeitgesteuert + bei Netz-Rückkehr + wenn der Tab wieder sichtbar wird
  // Nur an/aus und Turnier, nicht am ganzen Profil hängen — das ändert sich bei jedem Abgleich
  const syncAn = !!syncCfg;
  const syncTid = syncCfg?.tournamentId;

  // Sperre je Turnier: nur ein Fenster gleicht ab
  useEffect(() => {
    if (!syncAn || !syncTid || !locksDa()) return;
    const ac = new AbortController();
    let freigeben: (() => void) | undefined;
    navigator.locks
      .request(`airmail-sync:${syncTid}`, { signal: ac.signal }, async () => {
        // Übernommen (z. B. das abgleichende Fenster wurde geschlossen): Profil frisch aus der Datenbank,
        // angeglichen an den eigenen Stand — die eigenen, noch nicht übernommenen Eingaben gehen mit
        const c = await loadSync(syncTid);
        const cur = cfgRef.current;
        if (c && cur && stateRef.current) {
          const next = outboxAusLog(c, stateRef.current.log);
          setCfg(next);
          setSyncStatus(next.outbox.length ? 'pending' : 'idle');
          if (next !== c) void saveSync(next);
        }
        halterRef.current = true;
        setHalter(true);
        await new Promise<void>((r) => (freigeben = r));
      })
      .catch(() => {
        /* abgebrochen — Fenster zu oder Sync aus */
      });
    return () => {
      ac.abort();
      halterRef.current = false;
      setHalter(false);
      freigeben?.();
    };
  }, [syncAn, syncTid, setCfg]);

  useEffect(() => {
    if (!syncAn || !halter) return;
    void syncNow();
    const iv = window.setInterval(() => void syncNow(), SYNC_INTERVAL_MS);
    const onOnline = () => void syncNow();
    const onVisible = () => document.visibilityState === 'visible' && void syncNow();
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(iv);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [syncAn, syncTid, halter, syncNow]);

  /**
   * „Wieder vorwärts": Was „Rückgängig" herausnimmt, wird hier aufgehoben, bis
   * eine neue Eingabe kommt — genau wie in einem Textprogramm. Harald war aus
   * der KO-Runde zu weit zurückgegangen und musste Ergebnisse neu eintragen;
   * das soll nicht mehr passieren.
   */
  const [redoStack, setRedoStack] = useState<ActionBody[]>([]);
  const redoing = useRef(false);

  const dispatch = useCallback(
    (body: ActionBody, opts?: { silent?: boolean }): DispatchResult => {
      const cur = stateRef.current;
      if (!cur) return { ok: false, error: 'Kein Turnier geladen', warnings: [] };
      const action = { ...body, at: Date.now(), actor: 'leitung', id: nextActionId() };
      const r = reduce(cur, action);
      if (!r.ok) {
        if (!opts?.silent) pushToast('error', r.error);
        return { ok: false, error: r.error, warnings: r.warnings };
      }
      persist(r.state);
      // Eine frische Eingabe beendet den Weg zurück — wie überall sonst auch.
      // Stille Aktionen zählen nicht dazu: Die automatische Feldzuweisung
      // schiebt ständig Spiele nach und hätte den Stapel sonst sofort geleert.
      if (!redoing.current && !opts?.silent) setRedoStack((st) => (st.length ? [] : st));
      if (!opts?.silent) for (const w of r.warnings) pushToast('warn', w);
      const cfg = cfgRef.current;
      if (cfg) {
        // Outbox synchron im Ref pflegen (Folge-Dispatches im selben Tick), Persistenz gebündelt am Ende des Takts
        setCfg({ ...cfg, outbox: [...cfg.outbox, action] });
        setSyncStatus('pending');
        if (syncing.current) waehrendSync.current.push(action);
        cfgSpeichern();
        if (halterRef.current) window.setTimeout(() => void syncNowRef.current(), 300);
      }
      return { ok: true, warnings: r.warnings };
    },
    [persist, pushToast, setCfg, cfgSpeichern],
  );

  const canUndo = !!tournament && !!lastEffective(tournament.log);

  /**
   * Rückgängig: Solange die letzte Aktion nur auf diesem Gerät liegt, fliegt sie
   * aus dem Protokoll. Ist sie schon synchronisiert (andere Geräte kennen sie),
   * wird stattdessen ein Widerruf protokolliert (`revert`, docs/14) — der wandert
   * wie jede Aktion zu allen Geräten.
   *
   * Während eines Abgleichs ist „nur auf diesem Gerät" nicht sicher: Die Aktion
   * kann gerade zum Server unterwegs sein. Vorher flog sie trotzdem aus der
   * Outbox — und kam mit dem Ergebnis des Abgleichs zurück, oder lag schon beim
   * Server und bei allen anderen Geräten. Jetzt:
   * - Eingaben, die erst während des Abgleichs kamen, sind sicher nicht
   *   unterwegs — sie fallen auch aus der Liste der Zwischen-Eingaben.
   * - Alles andere wartet das Ende des Abgleichs ab und entscheidet dann
   *   (noch in der Outbox → herausnehmen, beim Server → Widerruf).
   * In einem Fenster, das nicht selbst abgleicht, ist nie sicher, ob das
   * abgleichende Fenster die Aktion schon geschickt hat — dort gilt der Widerruf.
   */
  const undoWartet = useRef(false);
  const undoRef = useRef<() => void>(() => undefined);
  const undo = useCallback(() => {
    const cur = stateRef.current;
    if (!cur) return;
    const last = lastEffective(cur.log);
    if (!last) return;
    const cfg = cfgRef.current;
    const isTail = cur.log[cur.log.length - 1] === last;
    // Für „Wieder vorwärts" merken — ohne Kennung und Zeit, das ist beim
    // erneuten Anwenden eine neue Aktion
    const { id: _id, at: _at, actor: _actor, ...wieder } = last as Action & { actor?: string };
    const fremdesFenster = !!cfg && !halterRef.current && cfg.role !== 'helfer';
    if (isTail && canUndoSynced(cfg, last) && !fremdesFenster) {
      if (cfg && syncing.current) {
        const dazwischen = waehrendSync.current.some((a) => a.id === last.id);
        if (!dazwischen) {
          if (!undoWartet.current && syncLauf.current) {
            undoWartet.current = true;
            void syncLauf.current.finally(() => {
              undoWartet.current = false;
              undoRef.current();
            });
          }
          return;
        }
        waehrendSync.current = waehrendSync.current.filter((a) => a.id !== last.id);
      }
      const r = reduce(cur, { type: 'undo', at: Date.now() });
      if (r.ok) {
        persist(r.state);
        const c = cfgRef.current;
        if (c && last.id) {
          setCfg({ ...c, outbox: c.outbox.filter((a) => a.id !== last.id) });
          cfgSpeichern();
        }
        setRedoStack((st) => [...st, wieder as ActionBody]);
        pushToast('ok', 'Rückgängig gemacht');
      } else pushToast('error', r.error);
      return;
    }
    if (!last.id) return pushToast('warn', 'Diese Aktion lässt sich nicht mehr rückgängig machen — bitte über „Korrektur“ ändern');
    redoing.current = true;
    const ok = dispatch({ type: 'revert', targetId: last.id }, { silent: true }).ok;
    redoing.current = false;
    if (ok) {
      setRedoStack((st) => [...st, wieder as ActionBody]);
      pushToast('ok', 'Rückgängig gemacht — geht auch an die anderen Geräte');
    } else pushToast('warn', 'Diese Aktion lässt sich nicht mehr rückgängig machen — bitte über „Korrektur“ ändern');
  }, [persist, pushToast, setCfg, cfgSpeichern, dispatch]);
  undoRef.current = undo;

  /**
   * Wieder vorwärts: die zuletzt zurückgenommene Eingabe erneut anwenden — als
   * neue Aktion auf dem jetzigen Stand. Passt sie nicht mehr (weil inzwischen
   * etwas anderes geschehen ist), sagt die App, woran es liegt, und der Eintrag
   * fällt aus dem Stapel; sonst stünde man in einer Sackgasse.
   */
  const redo = useCallback(() => {
    const body = redoStack[redoStack.length - 1];
    if (!body) return;
    redoing.current = true;
    const r = dispatch(body, { silent: true });
    redoing.current = false;
    setRedoStack((st) => st.slice(0, -1));
    if (r.ok) pushToast('ok', 'Wieder vorwärts');
    else pushToast('warn', tr('Geht nicht mehr vorwärts: {e}', { e: tm(r.error ?? '') }));
  }, [dispatch, pushToast, redoStack]);

  const replace = useCallback((next: Tournament) => persist(next), [persist]);

  const patchSyncCfg = useCallback(
    (patch: Partial<Omit<SyncConfig, 'tournamentId' | 'outbox' | 'remote' | 'seq'>>) => {
      const cur = cfgRef.current;
      if (!cur) return;
      setCfg({ ...cur, ...patch });
      if (syncing.current) patchWaehrend.current = { ...patchWaehrend.current, ...patch };
      // Fenster ohne eigenen Abgleich: dem abgleichenden Fenster sagen — es speichert
      if (halterRef.current) cfgSpeichern();
      else senden({ id, patch });
    },
    [id, setCfg, cfgSpeichern],
  );
  const dismissToast = useCallback((tid: number) => setToasts((ts) => ts.filter((x) => x.id !== tid)), []);

  const enable = useCallback(
    async (apiUrl: string) => {
      const cur = stateRef.current;
      if (!cur) return;
      // Alle bisherigen Aktionen bekommen frische IDs in Protokoll-Reihenfolge — der
      // Server dedupliziert je Gerät über eine monotone Laufnummer, ältere IDs
      // späterer Aktionen würden sonst als Duplikate verworfen
      const withIds = { ...cur, log: cur.log.map((a) => ({ ...a, id: nextActionId() })) };
      persist(withIds);
      const cfg = await enableSyncRemote(withIds, apiUrl);
      // Was während des Anlegens eingetragen oder zurückgenommen wurde, gleich mit angleichen
      const angeglichen = stateRef.current ? outboxAusLog(cfg, stateRef.current.log) : cfg;
      setCfg(angeglichen);
      if (angeglichen !== cfg) void saveSync(angeglichen);
      setSyncStatus('pending');
      pushToast('ok', 'Cloud-Sync aktiviert');
      window.setTimeout(() => void syncNowRef.current(), 100);
    },
    [persist, pushToast, setCfg],
  );

  const disable = useCallback(async () => {
    const cfg = cfgRef.current;
    if (!cfg) return;
    await removeSync(cfg.tournamentId);
    cfgRef.current = null;
    setSyncCfg(null);
    setSyncStatus('off');
    senden({ id: cfg.tournamentId, aus: true });
    pushToast('ok', 'Cloud-Sync für dieses Gerät beendet — Daten bleiben lokal');
  }, [pushToast]);

  const importExternal = useCallback(
    (actions: Action[]): ExternalMergeResult | null => {
      const cur = stateRef.current;
      // Ohne geladenes Turnier gibt es nichts, wohin — der Aufrufer sieht „nichts übernommen", keinen Schein-Zustand
      if (!cur) return null;
      const r = mergeExternal(cur, actions);
      if (r.fresh.length === 0) return r;
      /*
       * Aktionen ohne Kennung bekommen sie **vor** dem Speichern — im Stand und
       * in der Outbox dieselbe. Vorher bekam nur die Outbox-Kopie eine, der
       * Stand behielt die Aktion ohne; der Abgleich hielt sie dann für fremd.
       */
      const neueIds = new Map<Action, Action>();
      for (const a of r.fresh) if (!a.id) neueIds.set(a, { ...a, id: nextActionId() });
      const state = neueIds.size ? { ...r.state, log: r.state.log.map((a) => neueIds.get(a) ?? a) } : r.state;
      persist(state);
      const cfg = cfgRef.current;
      if (cfg) {
        const withIds = r.fresh.map((a) => neueIds.get(a) ?? a);
        setCfg({ ...cfg, outbox: [...cfg.outbox, ...withIds] });
        if (syncing.current) waehrendSync.current.push(...withIds);
        cfgSpeichern();
        if (halterRef.current) window.setTimeout(() => void syncNowRef.current(), 300);
      }
      return { ...r, state, fresh: r.fresh.map((a) => neueIds.get(a) ?? a) };
    },
    [persist, setCfg, cfgSpeichern],
  );

  const turnier = useMemo<TurnierCtx | null>(
    () =>
      tournament
        ? {
            tournament,
            dispatch,
            undo,
            canUndo,
            redo,
            canRedo: redoStack.length > 0,
            notify: pushToast,
            replace,
            lastSavedAt,
            patchSyncCfg,
            importExternal,
          }
        : null,
    [tournament, dispatch, undo, canUndo, redo, redoStack.length, pushToast, replace, lastSavedAt, patchSyncCfg, importExternal],
  );
  const sync = useMemo<SyncState>(() => {
    const s: SyncState = {
      cfg: syncCfg,
      status: syncCfg ? syncStatus : 'off',
      pending: syncCfg?.outbox.length ?? 0,
      syncNow,
      enable,
      disable,
    };
    if (syncCfg?.lastSyncAt !== undefined) s.lastSyncAt = syncCfg.lastSyncAt;
    if (syncCfg?.lastError !== undefined) s.lastError = syncCfg.lastError;
    return s;
  }, [syncCfg, syncStatus, syncNow, enable, disable]);
  const toastCtx = useMemo<ToastCtx>(() => ({ toasts, dismissToast }), [toasts, dismissToast]);

  if (ladeFehler)
    return (
      <div className="p-8 grid gap-3 max-w-xl" role="alert">
        <div className="font-bold text-red">{t('Turnier konnte nicht geladen werden.')}</div>
        <div className="text-sm text-muted">{t('Der Speicher des Browsers meldet: {e}', { e: ladeFehler })}</div>
        <div>
          <Button onClick={() => setLadeVersuch((n) => n + 1)}>{t('Neu laden')}</Button>
        </div>
      </div>
    );
  if (tournament === undefined) return <div className="p-8 text-dim">{t('Lade …')}</div>;
  if (tournament === null || !turnier) return <>{fallback}</>;
  return (
    <TurnierContext.Provider value={turnier}>
      <SyncContext.Provider value={sync}>
        <ToastContext.Provider value={toastCtx}>{children}</ToastContext.Provider>
      </SyncContext.Provider>
    </TurnierContext.Provider>
  );
}

/**
 * Alles auf einmal: Stand, Aktionen, Sync-Zustand und Hinweise. Rendert bei
 * jeder Änderung an einem davon neu — wer nur Stand und Aktionen braucht,
 * nimmt `useTurnier()`, wer nur den Sync-Zustand braucht, `useSync()`.
 */
export function useTournament(): Ctx {
  const turnier = useContext(TurnierContext);
  const sync = useContext(SyncContext);
  const toast = useContext(ToastContext);
  if (!turnier || !sync || !toast) throw new Error('useTournament außerhalb von TournamentProvider');
  return useMemo(() => ({ ...turnier, ...toast, sync }), [turnier, toast, sync]);
}

/** Nur Stand und Aktionen — rendert nicht bei jedem Abgleich neu */
export function useTurnier(): TurnierCtx {
  const ctx = useContext(TurnierContext);
  if (!ctx) throw new Error('useTurnier außerhalb von TournamentProvider');
  return ctx;
}

/** Nur der Sync-Zustand (Ampel, Konto-Seite, Einstellungen) */
export function useSync(): SyncState {
  const ctx = useContext(SyncContext);
  if (!ctx) throw new Error('useSync außerhalb von TournamentProvider');
  return ctx;
}

/** Nur die Hinweise (unten rechts) */
export function useToasts(): ToastCtx {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToasts außerhalb von TournamentProvider');
  return ctx;
}

/** Erster Bewerb — v1 hat genau einen (Mehrere Bewerbe: M4) */
export function useCompetition() {
  const { tournament } = useTurnier();
  const comp = tournament.competitions[0];
  if (!comp) throw new Error('Turnier ohne Bewerb');
  return comp;
}
